// src/utils/studentDataAccess.js
//
// Pass 82D §21/§30/§36/§48 — ONE canonical, ORGANIZATION-SCOPED, READ-ONLY student data-access layer. Two live
// organizations now exist, so a student's private surfaces (videos/lessons/reviews) MUST be scoped by (studentId +
// organizationId): a student whose Auth uid is in two orgs must never get a blended private data set. These helpers
// scope by the student's Auth uid AND filter to the intended organization (in-memory org filter → no new composite
// index required, and it fixes the effective read scope). They perform ZERO writes — the Student Preview reuses them so
// merely opening a student's Home/Videos/Lessons/Progress can never mutate anything (no lesson auto-claim, no
// mark-as-read). The identity concepts stay DISTINCT: `studentAuthUid` here is the Firebase Auth uid; it is NOT a
// commercial `clientId` and NOT necessarily a `requesterUserId` — this module never conflates them.

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { lessonProjectionId } from './lessonProjection';

const orgMatch = (rec, organizationId) => !organizationId || String(rec.organizationId || '') === String(organizationId);
const str = (v) => String(v == null ? '' : v).trim();

// ── Pass 85.1A — MODERN participant + org scoped reads (query-boundary org scope, NOT in-memory). The identity is the
// canonical `participantId` (a coaching participant explicitly linked to THIS org), never the raw Auth uid, so Org A's
// participant can never read Org B's data and the 75 LEGACY tenantless videos (no organizationId, no participantId) are
// structurally excluded — they can never appear inside a modern per-org student view.

// Videos for a participant in one org: `videos where organizationId==org AND participantId==pid` (composite index).
export async function loadParticipantVideos(db, { participantId = '', organizationId = '' } = {}) {
  const pid = str(participantId); const org = str(organizationId);
  if (!pid || !org) return [];
  const snap = await getDocs(query(
    collection(db, 'videos'),
    where('organizationId', '==', org),
    where('participantId', '==', pid),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// A legacy tenantless video = no organizationId (and no modern participantId). These stay in the old owner/assignee
// compatibility path ONLY and must never contaminate a modern per-org query.
export const isTenantlessLegacyVideo = (v = {}) => !str(v.organizationId) && !str(v.participantId);

// Lessons a participant may see = their explicit `lessonParticipantLinks` in this org, resolved to the SAFE PUBLISHED
// PROJECTION (`lessonProjections/{org::lessonId::participantId}`). Only EXPLICITLY published lessons appear; the whole
// lessonInstance is never read. STRICTLY read-only (no email-claim writes). Returns the safe projection docs.
export async function loadParticipantLessonProjections(db, { participantId = '', organizationId = '' } = {}) {
  const pid = str(participantId); const org = str(organizationId);
  if (!pid || !org) return [];
  const linkSnap = await getDocs(query(
    collection(db, PLATFORM_COLLECTIONS.LESSON_PARTICIPANT_LINKS),
    where('organizationId', '==', org),
    where('participantId', '==', pid),
  ));
  const lessonIds = linkSnap.docs.map((d) => str(d.data().lessonId)).filter(Boolean);
  const projections = await Promise.all(lessonIds.map(async (lessonId) => {
    const id = lessonProjectionId(org, lessonId, pid);
    if (!id) return null;
    const p = await getDoc(doc(db, PLATFORM_COLLECTIONS.LESSON_PROJECTIONS, id));
    return p.exists() ? { id: p.id, ...p.data() } : null; // unpublished lesson → no projection → not shown
  }));
  return projections.filter(Boolean);
}

// Videos a student OWNS, scoped to one organization. (Fixes the pre-82D `where('userId','==',uid)`-only reads that
// ignored organizationId — StudentDashboardShell / StudentVideosPage / MySkillsPage.)
export async function loadStudentVideos(db, { studentAuthUid = '', organizationId = '' } = {}) {
  if (!studentAuthUid) return [];
  const snap = await getDocs(query(collection(db, 'videos'), where('userId', '==', studentAuthUid)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((v) => orgMatch(v, organizationId));
}

// Client-side org filter for a list already fetched by uid — lets existing components keep their query and just add the
// tenancy scope (the narrow §48 fix) without a schema/index change.
export function scopeToOrganization(records = [], organizationId = '') {
  return (records || []).filter((r) => orgMatch(r, organizationId));
}

// Lessons a student participates in, scoped to one organization, STRICTLY READ-ONLY (no email-match auto-claim writes,
// unlike loadVisibleLessonsForStudent). Used by the Student Preview so opening Lessons never writes.
export async function loadStudentLessonsReadOnly(db, { studentAuthUid = '', organizationId = '' } = {}) {
  if (!studentAuthUid) return [];
  const snap = await getDocs(query(collection(db, 'lessonInstances'), where('studentId', '==', studentAuthUid)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((l) => orgMatch(l, organizationId));
}

// Student-visible reviews (clientNotes) — already org-scoped by design; kept here so the preview reads through ONE
// module. Requires the composite the app already uses (clientUserId + organizationId + visibility).
export async function loadStudentReviewsReadOnly(db, { studentAuthUid = '', organizationId = '' } = {}) {
  if (!studentAuthUid || !organizationId) return [];
  const snap = await getDocs(query(
    collection(db, 'clientNotes'),
    where('clientUserId', '==', studentAuthUid),
    where('organizationId', '==', organizationId),
    where('visibility', '==', 'student'),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Build a truthful DATA-HEALTH summary for the Student Preview QA panel (§28). Counts + linkage warnings, derived from
// canonical records only. NEVER fabricates identity. `clientLink` (an optional resolved commercial clientId) is passed
// separately — a video/lesson uploaded under the Auth uid is participant evidence, not a commercial clientId.
export function buildStudentDataHealth({ studentAuthUid = '', organizationId = '', videos = [], lessons = [], reviews = [], clientLink = null } = {}) {
  const warnings = [];
  const orgOf = (r) => String(r.organizationId || '');
  const tenantless = [...videos, ...lessons].filter((r) => !orgOf(r)).length;
  if (tenantless > 0) warnings.push({ code: 'tenantless_records', label: `${tenantless} record(s) with no organization recorded (legacy / tenant not recorded)` });
  const crossOrg = [...videos, ...lessons, ...reviews].filter((r) => orgOf(r) && organizationId && orgOf(r) !== organizationId).length;
  if (crossOrg > 0) warnings.push({ code: 'cross_org_records', label: `${crossOrg} record(s) belong to a different organization (excluded from this view)` });
  const reviewsWithoutLesson = reviews.filter((r) => !r.relatedLessonId).length;
  if (reviewsWithoutLesson > 0) warnings.push({ code: 'review_without_lesson', label: `${reviewsWithoutLesson} review(s) not linked to a lesson` });
  const videosWithoutOrg = videos.filter((v) => !orgOf(v)).length;
  if (videosWithoutOrg > 0) warnings.push({ code: 'video_without_org', label: `${videosWithoutOrg} video(s) without an organization` });
  const lessonsWithoutStudentLink = lessons.filter((l) => !l.studentId).length;
  if (lessonsWithoutStudentLink > 0) warnings.push({ code: 'lesson_without_student_link', label: `${lessonsWithoutStudentLink} lesson(s) without an explicit student link` });
  if (!clientLink) warnings.push({ code: 'unlinked_client_identity', label: 'No commercial client identity (clientId) is linked to this Auth account' });

  return {
    studentAuthUid,
    organizationId,
    counts: {
      videos: videos.filter((v) => orgMatch(v, organizationId)).length,
      lessons: lessons.filter((l) => orgMatch(l, organizationId)).length,
      reviews: reviews.length,
    },
    clientLinked: !!clientLink,
    warnings,
  };
}
